import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import {
  AssignmentInd as AssignmentIndIcon,
  Close as CloseIcon
} from '@material-ui/icons';

import AppSnackbar from '../../../common/AppSnackbar';
import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { User, UserPermissionFilter } from '../../../user/User.interface';
import UserLink from '../../users/components/UserLink';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import TaskAssignDialog from '../dialogs/TaskAssignDialog';
import { Task } from '../Task.interface';
import { useUpdateTask } from '../Task.hooks';

export interface TaskAssignCardProps {
  task: Task;
  disabled?: boolean;
  userFilter?: UserPermissionFilter;
}

const TaskAssignCard: React.FC<TaskAssignCardProps> = props => {
  const { task, disabled, userFilter } = props;
  const [openRemoveDialog, setOpenRemowDialog] = useState<boolean>(false);
  const [assignedUser, setAssignedUser] = useState<User | undefined>(task.user);
  const [selectedTask, setSelectedTask] = useState<Task>();
  const { updatedTask, isUpdating, updateError, updateTask } = useUpdateTask(
    task.id || 0
  );
  const { isFieldDisabled } = useTaskEntityFields(task);
  const disabledAssignTask = disabled || isFieldDisabled('assignTask');

  const userName: string = useMemo(() => {
    const { firstName = '', lastName = '' } = assignedUser || {};
    return `${firstName} ${lastName}`;
  }, [assignedUser]);

  useEffect(() => {
    setAssignedUser(task.user);
  }, [task]);

  useEffect(() => {
    updatedTask && setAssignedUser(updatedTask.user);
  }, [updatedTask]);

  return (
    <Card>
      {isUpdating && <LinearProgress />}
      <CardTitle>
        <AssignmentIndIcon />
        Assigned
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {assignedUser ? (
              <TableRow
                hover={!disabledAssignTask}
                onClick={() => {
                  !disabledAssignTask && setSelectedTask(task);
                }}
              >
                <TableCell>
                  <UserLink
                    type='name'
                    disableLink={true}
                    user={assignedUser}
                  />
                </TableCell>
                <TableCell padding='checkbox'>
                  <IconButton
                    size='small'
                    disabled={disabledAssignTask}
                    onClick={event => {
                      event.stopPropagation();
                      setOpenRemowDialog(true);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  <Link
                    component='button'
                    variant='body2'
                    disabled={disabledAssignTask}
                    style={disabledAssignTask ? { color: 'grey' } : {}}
                    onClick={() => {
                      setSelectedTask(task);
                    }}
                  >
                    Assign
                  </Link>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedTask && (
        <TaskAssignDialog
          open={Boolean(selectedTask)}
          taskId={selectedTask.id!}
          userFilter={userFilter}
          onCancel={() => setSelectedTask(undefined)}
          onAssign={data => {
            setAssignedUser(data.user);
            setSelectedTask(undefined);
          }}
        />
      )}

      <ConfirmDialog
        title='Unassign User'
        confirmLabel='Confirm'
        open={openRemoveDialog && !!assignedUser}
        message={`Are you sure you want to unassign "${userName}" from this Task?`}
        onCancel={() => setOpenRemowDialog(false)}
        onConfirm={() => {
          updateTask({ userId: null });
          setOpenRemowDialog(false);
        }}
      />

      <AppSnackbar severity='error' open={!!updateError}>
        {updateError?.message || 'An error occurred.'}
      </AppSnackbar>
    </Card>
  );
};

export default TaskAssignCard;
